import axios, { AxiosRequestConfig } from 'axios'
import { serverUrl } from '../constants'
import { getLocal, setLocals } from '../utils/local'
import logout from '../utils/logout'
import { showToast } from '../utils/toast'
export * as endpoints from './endpoints'

interface IAuthCallData {
  url: string
  data?: any
  config?: AxiosRequestConfig
  method: 'get' | 'post' | 'put' | 'delete' | 'patch'
}

interface RefreshTokenResponse {
  tokens: {
    access: { token: string }
    refresh: { token: string }
  }
}

const axiosInstance = axios.create({
  baseURL: serverUrl,
})

axiosInstance.interceptors.response.use(
  async function (data) {
    return data.data
  },
  async function (err) {
    const { config, response } = err
    if (response?.status === 401) {
      // get refresh token
      // TODO: keep refreshToken in session
      const refreshToken = getLocal('refreshToken')
      if (!refreshToken) {
        logout()
      }
      // refresh token
      // TODO: refresh token
      const data = await postFunc<{ refreshToken: string }, RefreshTokenResponse>(
        '/auth/refresh-tokens',
        {
          refreshToken,
        }
      ).catch(() => {
        logout()
      })

      if (data) {
        // recall
        setLocals({
          token: data.tokens.access.token,
          refreshToken: data.tokens.refresh.token,
        })
        const token = data.tokens.access.token
        config.headers['x-auth-token'] = token
        return axiosInstance(config)
      }
    }
    
    if (response?.status === 403) {
      const message = response?.data?.message

      if (message !== 'Please verify account.') {
        showToast({
          title: 'Restricted Access',
          description:
            'This action is restricted. Please contact the administrator for access.',
          status: 'error',
          duration: 5000,
          isClosable: true,
        })
      }
    }

    return Promise.reject(err?.response?.data || err)
  }
)

export function getFunc<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return axiosInstance.get(url, {
    ...config,
  })
}

export function postFunc<T, R>(
  url: string,
  data?: T,
  config?: AxiosRequestConfig
): Promise<R> {
  return axiosInstance.post(url, data, {
    ...config,
  })
}

export function updateFunc<T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return axiosInstance.put(url, data, {
    ...config,
  })
}
export function patchFunc<T>(
  url: string,
  data?: any,
  config?: AxiosRequestConfig
): Promise<T> {
  return axiosInstance.patch(url, data, {
    ...config,
  })
}
export function deleteFunc<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  return axiosInstance.delete(url, {
    ...config,
  })
}

export function authCall<T>(params: IAuthCallData): Promise<T> {
  const token = getLocal('token')
  switch (params.method) {
    case 'get':
      return getFunc<T>(params.url, {
        ...(params.config || {}),
        headers: {
          ...(params?.config?.headers || {}),
          'x-auth-token': token,
        },
      })
    case 'post':
      return postFunc<IAuthCallData['data'], T>(params.url, params.data, {
        ...(params.config || {}),
        headers: {
          ...(params?.config?.headers || {}),
          'x-auth-token': token,
        },
      })
    case 'put':
      return updateFunc<T>(params.url, params.data, {
        ...(params.config || {}),
        headers: {
          ...(params?.config?.headers || {}),
          'x-auth-token': token,
        },
      })
    case 'patch':
      return patchFunc<T>(params.url, params.data, {
        ...(params.config || {}),
        headers: {
          ...(params?.config?.headers || {}),
          'x-auth-token': token,
        },
      })
    case 'delete':
      return deleteFunc<T>(params.url, {
        ...(params.config || {}),
        headers: {
          ...(params?.config?.headers || {}),
          'x-auth-token': token,
        },
      })
  }
}

export function changeStaffPassword(
  oldPassword: string,
  newPassword: string
): Promise<void> {
  const token = getLocal('token')
  return postFunc(
    'staffs/me/password',
    { oldPassword, newPassword },
    {
      headers: {
        'x-auth-token': token,
      },
    }
  )
}
